import React, { useState, useEffect } from "react";
import ApiService from "../services/ApiService";
import "./DisabledDateManagement.css";

const DisabledDateManagement = () => {
  const [disabledDates, setDisabledDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDate, setEditingDate] = useState(null);
  const [formData, setFormData] = useState({
    date: "",
    reason: "",
  });

  useEffect(() => {
    fetchDisabledDates();
  }, []);

  const fetchDisabledDates = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAllDisabledDates();
      setDisabledDates(response.data || []);
      setError("");
    } catch (error) {
      console.error("Error fetching disabled dates:", error);
      setError("Failed to fetch disabled dates");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date: "",
      reason: "",
    });
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingDate(null);
    setError("");
    setSuccess("");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddDate = async (e) => {
    e.preventDefault();

    if (!formData.date) {
      setError("Date is required");
      return;
    }

    // Check if date is in the past
    const selectedDate = new Date(formData.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      setError("Cannot disable dates in the past");
      return;
    }

    try {
      setLoading(true);
      await ApiService.createDisabledDate(formData);
      setSuccess("Date disabled successfully!");
      await fetchDisabledDates();
      resetForm();
    } catch (error) {
      console.error("Error creating disabled date:", error);
      setError(error.message || "Failed to disable date");
    } finally {
      setLoading(false);
    }
  };

  const handleEditDate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await ApiService.updateDisabledDate(editingDate.id, {
        reason: formData.reason,
      });
      setSuccess("Disabled date updated successfully!");
      await fetchDisabledDates();
      resetForm();
    } catch (error) {
      console.error("Error updating disabled date:", error);
      setError(error.message || "Failed to update disabled date");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDate = async (id) => {
    if (
      !window.confirm("Are you sure you want to enable this date for booking?")
    ) {
      return;
    }

    try {
      setLoading(true);
      await ApiService.deleteDisabledDate(id);
      setSuccess("Date enabled successfully!");
      await fetchDisabledDates();
    } catch (error) {
      console.error("Error deleting disabled date:", error);
      setError(error.message || "Failed to enable date");
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (date) => {
    setEditingDate(date);
    setFormData({
      date: date.date,
      reason: date.reason || "",
    });
    setShowEditModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatDay = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        weekday: "long",
      });
    } catch (error) {
      return "";
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  return (
    <div className="admin-disabled-date-management">
      <div className="admin-disabled-header-section">
        <div className="admin-disabled-header-content">
          <h1>Disabled Date Management</h1>
          <p>Manage dates when bookings are not allowed</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="admin-disabled-add-btn"
        >
          <span>🚫</span>
          Disable New Date
        </button>
      </div>

      {error && <div className="admin-disabled-error-message">{error}</div>}
      {success && (
        <div className="admin-disabled-success-message">{success}</div>
      )}

      <div className="admin-disabled-dates-container">
        {loading ? (
          <div className="admin-disabled-loading-container">
            <div className="admin-disabled-loading-spinner"></div>
            <p>Loading disabled dates...</p>
          </div>
        ) : (
          <>
            {disabledDates.length === 0 ? (
              <div className="admin-disabled-no-dates">
                <h3>No disabled dates found</h3>
                <p>All dates are currently available for booking.</p>
              </div>
            ) : (
              <div className="admin-disabled-dates-grid">
                {disabledDates.map((date) => (
                  <div key={date.id} className="admin-disabled-date-card">
                    <div className="admin-disabled-date-header">
                      <div className="admin-disabled-date-info">
                        <h3>{formatDate(date.date)}</h3>
                        <p className="admin-disabled-date-day">
                          {formatDay(date.date)}
                        </p>
                      </div>
                      <div className="admin-disabled-date-actions">
                        <button
                          onClick={() => openEditModal(date)}
                          className="admin-disabled-edit-btn"
                          title="Edit reason"
                        >
                          <span>✏️</span>
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteDate(date.id)}
                          className="admin-disabled-delete-btn"
                          title="Enable date"
                        >
                          <span>✅</span>
                          Enable
                        </button>
                      </div>
                    </div>

                    {date.reason && (
                      <div className="admin-disabled-date-reason">
                        "{date.reason}"
                      </div>
                    )}

                    <div className="admin-disabled-date-meta">
                      <div>Created: {formatDateTime(date.createdAt)}</div>
                      {date.createdBy && <div>By: {date.createdBy}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Date Modal */}
      {showAddModal && (
        <div className="admin-disabled-modal-overlay">
          <div className="admin-disabled-modal-content">
            <div className="admin-disabled-modal-header">
              <h2>Disable New Date</h2>
              <button onClick={resetForm} className="admin-disabled-close-btn">
                ×
              </button>
            </div>
            <div className="admin-disabled-modal-body">
              <form onSubmit={handleAddDate}>
                <div className="admin-disabled-form-group">
                  <label htmlFor="date">Date *</label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    min={getMinDate()}
                    required
                    className="admin-disabled-form-input"
                  />
                </div>

                <div className="admin-disabled-form-group">
                  <label htmlFor="reason">Reason (Optional)</label>
                  <textarea
                    id="reason"
                    name="reason"
                    value={formData.reason}
                    onChange={handleInputChange}
                    placeholder="Provide a reason for disabling this date..."
                    className="admin-disabled-form-textarea"
                    rows={3}
                  />
                </div>

                <div className="admin-disabled-modal-actions">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="admin-disabled-cancel-btn"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="admin-disabled-save-btn"
                  >
                    {loading ? "Disabling..." : "Disable Date"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Date Modal */}
      {showEditModal && editingDate && (
        <div className="admin-disabled-modal-overlay">
          <div className="admin-disabled-modal-content">
            <div className="admin-disabled-modal-header">
              <h2>Edit Disabled Date</h2>
              <button onClick={resetForm} className="admin-disabled-close-btn">
                ×
              </button>
            </div>
            <div className="admin-disabled-modal-body">
              <form onSubmit={handleEditDate}>
                <div className="admin-disabled-form-group">
                  <label>Date</label>
                  <div
                    className="admin-disabled-form-input"
                    style={{
                      background: "rgba(255, 255, 255, 0.05)",
                      cursor: "not-allowed",
                    }}
                  >
                    {formatDate(editingDate.date)} (
                    {formatDay(editingDate.date)})
                  </div>
                  <small
                    style={{
                      color: "rgba(255, 255, 255, 0.6)",
                      fontSize: "0.8rem",
                    }}
                  >
                    Date cannot be changed. To change the date, delete this
                    entry and create a new one.
                  </small>
                </div>

                <div className="admin-disabled-form-group">
                  <label htmlFor="reason">Reason (Optional)</label>
                  <textarea
                    id="reason"
                    name="reason"
                    value={formData.reason}
                    onChange={handleInputChange}
                    placeholder="Provide a reason for disabling this date..."
                    className="admin-disabled-form-textarea"
                    rows={3}
                  />
                </div>

                <div className="admin-disabled-modal-actions">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="admin-disabled-cancel-btn"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="admin-disabled-save-btn"
                  >
                    {loading ? "Updating..." : "Update Reason"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisabledDateManagement;
